/**
 * Canonical validation for a single sustainability-indicator value (PLT-5088).
 *
 * Lives in `@upright/common` so the SAME rules run on the frontend (the chat sign-off card) and
 * server-side at commit (the batch-apply endpoint). Each indicator's expected value type comes from
 * `SUSTAINABILITY_INDICATOR_METADATA`:
 *  - `percentage` values must be in [0, 100];
 *  - `integer` values (counts of incidents, fatalities, …) must be whole and non-negative.
 * Every indicator value is non-negative.
 */
import { DisclosureDataPointId, SUSTAINABILITY_INDICATOR_METADATA } from './types';

/**
 * Returns the list of validation errors for a single indicator value (empty = valid). `value` is the
 * raw number the user/agent supplied; the indicator's metadata decides whether it is a percentage or
 * an integer count.
 */
export function validateSustainabilityIndicatorValue(disclosureId: DisclosureDataPointId, value: number): string[] {
  const errors: string[] = [];
  const meta = SUSTAINABILITY_INDICATOR_METADATA[disclosureId];

  if (!Number.isFinite(value)) {
    errors.push('Value must be a number.');
    return errors;
  }
  if (value < 0) {
    errors.push('Value cannot be negative.');
  }
  if (meta.valueType === 'percentage' && value > 100) {
    errors.push('Percentage cannot exceed 100.');
  }
  if (meta.valueType === 'integer' && !Number.isInteger(value)) {
    errors.push('Value must be a whole number.');
  }
  return errors;
}
